import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {TrashIcon} from '@heroicons/react/24/outline';

import usePageTitle from '../../../../hooks/usePageTitle';
import {useBreadcrumbs} from '../../../../contexts/BreadcrumbContext';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import {
  fetchResources,
  fetchUsers,
  isMarketOwnerAccount,
  selectResources,
  selectResourcesLoaded,
  selectUsersList,
} from '../../../../slices/userSlice';
import {
  createPaymentGroup,
  deletePaymentGroup,
  fetchPaymentGroups,
  setResourceGroup,
  updatePaymentGroup,
} from '../../../../slices/paymentGroupsSlice';

// The mutation thunks reject with the flattened backend message (a string),
// so a failed save must read the payload itself, not `error.message`.
const errorText = (error, fallback) => {
  if (typeof error === 'string' && error) return error;
  return error?.message || fallback;
};

const inputClass = 'w-full max-w-60 rounded-md border border-gray-300 dark:border-gray-600 '
  + 'bg-white dark:bg-gray-800 px-2 py-1 text-sm text-gray-900 dark:text-gray-100 '
  + 'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

export default function PaymentGroups() {
  usePageTitle('Payment groups');
  useBreadcrumbs(
    [{label: 'Forecasters', href: '/users/payments'}, {label: 'Payment groups'}],
    'Bundle a market owner\'s resources into one payment email per forecaster');

  const dispatch = useDispatch();
  const groups = useSelector((state) => state.paymentGroups.groups.data);
  const groupsStatus = useSelector((state) => state.paymentGroups.groups.status);
  const resources = useSelector(selectResources);
  const usersList = useSelector(selectUsersList);
  const resourcesLoaded = useSelector(selectResourcesLoaded);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [newOwner, setNewOwner] = useState('');
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    dispatch(fetchPaymentGroups());
    dispatch(fetchResources());
    // Needed to tell market owner accounts apart from admin/forecaster ones:
    // the resources payload carries no role information.
    dispatch(fetchUsers());
  }, [dispatch]);

  const groupOfResource = (resourceId) =>
    groups.find((g) => g.resources.some((r) => r.id === resourceId))?.id ?? '';

  // The inputs are uncontrolled, so a rejected save (for example a duplicate
  // label) must put the stored value back: otherwise the box keeps showing
  // text the backend never accepted.
  const onFieldBlur = (group, field, input) => {
    const stored = group[field] ?? '';
    if (stored === input.value) return;
    dispatch(updatePaymentGroup({id: group.id, [field]: input.value}))
      .unwrap()
      .then(() => {
        toast.success('Saved.');
        dispatch(fetchPaymentGroups());
      })
      .catch((error) => {
        input.value = stored;
        toast.error(errorText(error, 'Save failed.'));
      });
  };

  const onMembershipChange = (resourceId, groupId) => {
    dispatch(setResourceGroup({resourceId, groupId: groupId || null}))
      .unwrap()
      .then(() => {
        toast.success('Membership updated. Applies to the next send.');
        dispatch(fetchPaymentGroups());
      })
      .catch((error) => toast.error(errorText(error, 'Update failed.')));
  };

  // Owners come from the resources list: a group is only usable by the
  // account that owns the resources it will hold, and a resource can only
  // join a group owned by its own account. Narrowed to market owner accounts
  // (the rule PaymentGroupSerializer enforces) so the select cannot offer an
  // owner the backend will reject after submit. If the users list has not
  // arrived, offer the unnarrowed owners rather than an empty select: the
  // backend rejection is surfaced as a toast either way.
  const distinctOwners = Array.from(new Map(
    resources.filter((r) => r.user).map((r) => [r.user, r.email || r.user]),
  ).entries()).map(([id, email]) => ({id, email}))
    .sort((a, b) => a.email.localeCompare(b.email));
  const marketOwnerIds = new Set((usersList || [])
    .filter(isMarketOwnerAccount).map((u) => u.id));
  const narrowedOwners = distinctOwners.filter((o) => marketOwnerIds.has(o.id));
  const owners = narrowedOwners.length > 0 ? narrowedOwners : distinctOwners;

  const onCreate = () => {
    const label = newLabel.trim();
    if (!newOwner || !label) return;
    dispatch(createPaymentGroup({owner: newOwner, label}))
      .unwrap()
      .then(() => {
        toast.success('Group created. Assign resources below.');
        setNewLabel('');
        dispatch(fetchPaymentGroups());
      })
      .catch((error) => toast.error(errorText(error, 'Create failed.')));
  };

  const onDelete = (group) => {
    setPendingDelete(null);
    dispatch(deletePaymentGroup(group.id))
      .unwrap()
      .then(() => {
        toast.success('Group deleted. Its resources are now ungrouped.');
        dispatch(fetchPaymentGroups());
        dispatch(fetchResources());
      })
      .catch((error) => toast.error(errorText(error, 'Delete failed.')));
  };

  return (
    <div className="space-y-6">
      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete payment group"
        description={pendingDelete
          ? `"${pendingDelete.label}" will be deleted. Its `
            + `${pendingDelete.resources.length} resource`
            + `${pendingDelete.resources.length === 1 ? '' : 's'} become ungrouped `
            + 'and go back to one email per resource. No payments are deleted.'
          : ''}
        confirmText="Delete group"
        onConfirm={() => onDelete(pendingDelete)}
        onCancel={() => setPendingDelete(null)}
      />

      <div className="bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Groups</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4 max-w-3xl">
          The label appears in the email subject ("[Predico] Payment details for
          July 2026 - <span className="italic">label</span>"). The CC address receives a
          copy of every forecaster email in the group; leave it empty to fall back to the
          market owner account. The minimum payout applies to a forecaster's combined
          total across every market in the group: below it, nothing is paid and the whole
          amount carries into the next month. Members' own thresholds are ignored while
          they belong to a group.
        </p>

        {groupsStatus === 'failed' ? (
          <p className="text-sm text-red-600 dark:text-red-400">
            Could not load the payment groups. Reload the page to try again.
          </p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {groupsStatus === 'succeeded'
              ? 'No payment groups yet. Every resource sends its own email per forecaster.'
              : 'Loading payment groups...'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200/70 dark:border-gray-600/70">
                <th className="py-2 pr-3 font-semibold">Label</th>
                <th className="py-2 pr-3 font-semibold">Owner</th>
                <th className="py-2 pr-3 font-semibold">Market owner CC</th>
                <th className="py-2 pr-3 font-semibold">Min payout (EUR)</th>
                <th className="py-2 pr-3 font-semibold">Members</th>
                <th className="py-2 font-semibold sr-only">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {groups.map((group) => (
                <tr key={group.id}>
                  <td className="py-2 pr-3">
                    <input
                      type="text"
                      defaultValue={group.label}
                      aria-label={`label for group ${group.label}`}
                      className={inputClass}
                      onBlur={(e) => onFieldBlur(group, 'label', e.target)}
                    />
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                    {group.owner_email || '-'}
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="email"
                      defaultValue={group.cc_email}
                      placeholder={group.owner_email}
                      aria-label={`CC for group ${group.label}`}
                      className={inputClass}
                      onBlur={(e) => onFieldBlur(group, 'cc_email', e.target)}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={group.min_payout_threshold}
                      aria-label={`minimum payout for group ${group.label}`}
                      className={inputClass}
                      onBlur={(e) => onFieldBlur(group, 'min_payout_threshold', e.target)}
                    />
                  </td>
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {group.resources.length} resource{group.resources.length === 1 ? '' : 's'}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setPendingDelete(group)}
                      title="Members become ungrouped"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-accent-600 dark:text-accent-400 hover:underline"
                    >
                      <TrashIcon className="h-4 w-4"/>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <select
            value={newOwner}
            aria-label="owner for the new group"
            className={inputClass}
            onChange={(e) => setNewOwner(e.target.value)}
          >
            <option value="">Market owner...</option>
            {owners.map((owner) => (
              <option key={owner.id} value={owner.id}>{owner.email}</option>
            ))}
          </select>
          <input
            type="text"
            value={newLabel}
            placeholder="new group label"
            aria-label="label for the new group"
            className={inputClass}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onCreate();
              }
            }}
          />
          <button
            type="button"
            onClick={onCreate}
            disabled={!newOwner || !newLabel.trim()}
            title={owners.length === 0
              ? 'Waiting for the resources list to load'
              : 'Create a group for the chosen market owner'}
            className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            + New group
          </button>
        </div>
      </div>

      <div className="bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Resource membership</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4 max-w-3xl">
          Ungrouped resources keep the current per-resource email. A resource can only
          join a group owned by the same account. Changes apply from the next send; past
          emails are unaffected.
        </p>

        {resources.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {resourcesLoaded ? 'No resources found.' : 'Loading resources...'}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200/70 dark:border-gray-600/70">
                <th className="py-2 pr-3 font-semibold">Resource</th>
                <th className="py-2 pr-3 font-semibold">Owner account</th>
                <th className="py-2 font-semibold">Payment group</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {resources.map((resource) => (
                <tr key={resource.id}>
                  <td className="py-2 pr-3 font-mono text-xs text-gray-900 dark:text-gray-100">
                    {resource.name}
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                    {resource.email || '-'}
                  </td>
                  <td className="py-2">
                    <select
                      value={groupOfResource(resource.id)}
                      aria-label={`group for ${resource.name}`}
                      className={inputClass}
                      onChange={(e) => onMembershipChange(resource.id, e.target.value)}
                    >
                      <option value="">Ungrouped (per-resource email)</option>
                      {groups.map((group) => (
                        <option key={group.id} value={group.id}>{group.label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
